import produce from 'immer';
import { uniqBy } from 'lodash';
import { handleActions } from 'redux-actions';
import { RECOMMEND_EVENT_TYPE } from '../../../../constants/service/app/recommendEventConstants';
import { setUncheckableTableTransferList, updateStoreWithPath } from '../../../../lib/common';
import { _LIST_PAGINATION_MODEL } from '../../app/models';
import actions from './actions';
import {
  RECOMMEND_EVENT_LIST_SEARCH_DATA_MODEL,
  RECOMMEND_EVENT_LIST_SEARCH_OPTION_MODEL,
  RECOMMEND_EVENT_SAVE_MODEL,
} from './models';

/**
 *  INITIAL STATE
 */
const initialState = {
  /**
   *  추천 기획전 리스트
   */
  recommendEventList: {
    /**
     *  리스트
     */
    list: [],
    /**
     *  검색 박스
     */
    searchData: RECOMMEND_EVENT_LIST_SEARCH_DATA_MODEL,
    /**
     *  검색 옵션
     */
    searchOption: RECOMMEND_EVENT_LIST_SEARCH_OPTION_MODEL,
    /**
     *  리스트 pagination
     */
    pagination: _LIST_PAGINATION_MODEL,
  },

  /**
   *  추천 기획전 상세
   */
  recommendEventDetail: {
    /**
     *  추천 기획전 정보
     */
    recommendEventInfo: {
      /**
       *  추천 기획전 식별 id
       */
      recommendEventId: null,
      /**
       *  추천인
       */
      adminName: null,
      /**
       *  추천 기획전 타입
       */
      type: RECOMMEND_EVENT_TYPE.SEARCH_RECOMMEND,
      /**
       *  추천 기획전 생성일
       */
      createDate: null,
      /**
       *  메모
       */
      memo: null,
    },
    /**
     *  추천 기획전 아이템 리스트
     */
    recommendEventItemList: [],
  },

  /**
   *  추천 기획전 등록/복사
   */
  recommendEventSave: RECOMMEND_EVENT_SAVE_MODEL,
};

/**
 *  REDUCER
 */
export default handleActions(
  {
    /**
     *  추천 기획전 데이터 변경하기 성공
     */
    [actions.SET_DATA]: (state, action) => {
      return produce(state, (draft) => {
        updateStoreWithPath(action.payload.path, action.payload.value, draft);
      });
    },

    /**
     *  추천 기획전 리스트 가져오기 성공
     */
    [actions.GET_RECOMMEND_EVENT_LIST_SUCCESS]: (state, action) => {
      return produce(state, (draft) => {
        draft.recommendEventList = {
          ...draft.recommendEventList,
          ...action.payload.recommendEventList,
        };
      });
    },

    /**
     *  추천 기획전 상세 요청하기 성공
     */
    [actions.GET_RECOMMEND_EVENT_DETAIL_SUCCESS]: (state, action) => {
      return produce(state, (draft) => {
        draft.recommendEventDetail = action.payload.recommendEventDetail;
      });
    },

    /**
     *  추천 기획전 복사하기 전 필요한 데이터 가져오기 성공
     */
    [actions.PREPARE_COPY_RECOMMEND_EVENT_SUCCESS]: (state, action) => {
      return produce(state, (draft) => {
        /**
         *  선택할 기획전 리스트
         */
        draft.recommendEventSave.recommendEventItemList.itemList = {
          ...draft.recommendEventSave.recommendEventItemList.itemList,
          ...action.payload.recommendEventSave.recommendEventItemList,
        };

        /**
         *  기획전 정보
         */
        draft.recommendEventSave.recommendEventInfo = action.payload.recommendEventSave.recommendEventInfo;

        /**
         *  선택된 기획전 리스트
         */
        draft.recommendEventSave.recommendEventItemList.selectedItemList = {
          list: action.payload.recommendEventSave.selectedRecommendEventItemList,
          idList: action.payload.recommendEventSave.selectedRecommendEventItemList.map((item) => item.id),
        };

        /**
         *  선택할 기획전 아이템 중에 이미 설정된 기획전 아이템인 경우에는
         *  기획전 아이템 선택 부분에서 이동을 할 수 없도록 하기
         */
        draft.recommendEventSave.recommendEventItemList.itemList.list = setUncheckableTableTransferList({
          list: draft.recommendEventSave.recommendEventItemList.itemList.list,
          selectedIdList: draft.recommendEventSave.recommendEventItemList.selectedItemList.idList,
        });
      });
    },

    /**
     *   추천 기획전 등록하기 전 필요한 데이터 가져오기
     */
    [actions.PREPARE_CREATE_RECOMMEND_EVENT_SUCCESS]: (state, action) => {
      return produce(state, (draft) => {
        /**
         *  선택할 기획전 리스트
         */
        draft.recommendEventSave.recommendEventItemList.itemList =
          action.payload.recommendEventSave.recommendEventItemList;
      });
    },

    /**
     *  추천 기획전에 연결할 아이템 리스트 요청하기 성공
     */
    [actions.GET_RECOMMEND_EVENT_ITEM_SUCCESS]: (state, action) => {
      return produce(state, (draft) => {
        /**
         *  선택할 기획전 리스트
         */
        draft.recommendEventSave.recommendEventItemList.itemList = {
          ...draft.recommendEventSave.recommendEventItemList.itemList,
          ...action.payload.recommendEventItemList,
        };

        /**
         *  선택할 기획전 아이템 중에 이미 설정된 기획전 아이템인 경우에는
         *  기획전 아이템 선택 부분에서 이동을 할 수 없도록 하기
         */
        draft.recommendEventSave.recommendEventItemList.itemList.list = setUncheckableTableTransferList({
          list: draft.recommendEventSave.recommendEventItemList.itemList.list,
          selectedIdList: draft.recommendEventSave.recommendEventItemList.selectedItemList.idList,
        });
      });
    },

    /**
     *  기획전 리스트 EXCEL 업로드 성공
     */
    [actions.SET_RECOMMEND_EVENT_ITEM_LIST_EXCEL_UPLOAD_SUCCESS]: (state, action) => {
      return produce(state, (draft) => {
        /**
         *  선택된 기획전 리스트
         */
        draft.recommendEventSave.recommendEventItemList.selectedItemList.list = action.payload.itemList;

        /**
         *  선택된 기획전 아이템의 중복 제거하기
         */
        draft.recommendEventSave.recommendEventItemList.selectedItemList.list = uniqBy(
          draft.recommendEventSave.recommendEventItemList.selectedItemList.list,
          'id',
        );

        draft.recommendEventSave.recommendEventItemList.selectedItemList.idList =
          draft.recommendEventSave.recommendEventItemList.selectedItemList.list.map((item) => item.id);

        /**
         *  선택할 기획전 아이템 중에 이미 설정된 기획전 아이템인 경우에는
         *  기획전 아이템 선택 부분에서 이동을 할 수 없도록 하기
         */
        draft.recommendEventSave.recommendEventItemList.itemList.list = setUncheckableTableTransferList({
          list: draft.recommendEventSave.recommendEventItemList.itemList.list,
          selectedIdList: draft.recommendEventSave.recommendEventItemList.selectedItemList.idList,
        });
      });
    },
  },
  initialState,
);
